import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { StatStrip, type StatItem } from "@/components/ui/page-header";
import { Icon } from "@/lib/icons";
import { useKitCopy } from "./use-kit-copy";

/**
 * Sober end-of-session screen: a check, a title, the session figures and two
 * ways out.
 *
 * ```tsx
 * <TaskSessionComplete
 *   title={t.validationCompleteTitle}
 *   description={t.validationCompleteSubtitle}
 *   stats={[{ label: "Validés", value: 8, tone: "success" }, { label: "Rejetés", value: 2 }]}
 *   onContinue={restart}
 *   onHome={goHome}
 * />
 * ```
 *
 * - `continueLabel` / `homeLabel` / `hint`: override the kit defaults.
 * - `onContinue` omitted: only the home button is shown.
 */
export function TaskSessionComplete({
  title,
  description,
  stats = [],
  continueLabel,
  homeLabel,
  hint,
  onContinue,
  onHome,
}: {
  title?: ReactNode;
  description?: ReactNode;
  stats?: StatItem[];
  continueLabel?: string;
  homeLabel?: string;
  hint?: ReactNode;
  onContinue?: () => void;
  onHome: () => void;
}) {
  const { copy } = useKitCopy();
  return (
    <div className="mx-auto flex w-full max-w-xl flex-col items-center px-4 py-14 text-center sm:px-6">
      <span className="grid size-12 place-items-center rounded-full bg-success/10 text-success">
        <Icon name="checkCircle" className="size-6" />
      </span>
      <h1 className="mt-5 text-2xl font-semibold tracking-tight text-foreground">{title ?? copy.sessionDoneTitle}</h1>
      {description ? <p className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">{description}</p> : null}
      {stats.length > 0 ? <StatStrip items={stats} className="mt-8 w-full text-left" /> : null}
      <div className="mt-8 flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
        {onContinue ? (
          <Button className="h-12 sm:min-w-44" onClick={onContinue}>
            {continueLabel ?? copy.sessionDoneContinue}
            <Icon name="arrowRight" />
          </Button>
        ) : null}
        <Button variant="outline" className="h-12 sm:min-w-44" onClick={onHome}>
          {homeLabel ?? copy.sessionDoneHome}
        </Button>
      </div>
      {hint ? <p className="mt-4 text-xs text-faint-foreground">{hint}</p> : null}
    </div>
  );
}
